import { IDomainResource } from '../Resource';
import { IReference } from '../Reference';
import { IIdentifier } from '../Identifier';
import { IContactPoint } from '../ContactPoint';
import { date, dateTime, integer, AdministrativeGender } from '../Primitives';
import { ICodeableConcept } from '../CodeableConcept';
import { IAttachment } from '../Attachment';
import { IPeriod } from '../Period';
import { IHumanName } from '../HumanName';
import { IAddress } from '../Address';
import { IBackboneElement } from '../BackboneElement';

export type PatientType = 'Patient';

export interface IPatientContactComponent {
  relationship: ICodeableConcept[];
  name: IHumanName;
  telecom: IContactPoint;
  address: IAddress;
  gender: AdministrativeGender;
  organization: IReference;
  period: IPeriod;
}

export interface IPatientAnimalComponent extends IBackboneElement {
  species: ICodeableConcept;
  breed?: ICodeableConcept;
  genderStatus?: ICodeableConcept;
}

export interface IPatientCommunicationComponent extends IBackboneElement {
  language: ICodeableConcept;
  preferred?: boolean;
}

export type PatientLinkType = 'replaced-by' | 'replaces' | 'refer' | 'seealso';
export interface IPatientLinkComponent extends IBackboneElement {
  other: IReference;
  type: PatientLinkType;
}

export const createPatient = (
  firstName: string,
  lastName: string,
  dob: string,
  gender: AdministrativeGender,
  mrn: string,
  id?: string,
  organizationId?: string,
): IPatient => {
  const patient: IPatient = {
    resourceType: 'Patient',
    id: id,
    active: true,
    name: [
      {
        use: 'official',
        family: lastName,
        given: [firstName],
        text: `${firstName} ${lastName}`,
      },
    ],
    birthDate: dob,
    gender,
    identifier: [
      {
        type: {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/v2/0203',
              code: 'MR',
            },
          ],
        },
        value: mrn,
      },
    ],
  };

  if (organizationId)
    patient.managingOrganization = {
      reference: `Organization/${organizationId}`,
    };
  return patient;
};

export const isPatient = (
  resource: object | null | undefined,
): resource is IPatient =>
  !!resource && (resource as IPatient).resourceType === 'Patient';
export interface IPatient extends IDomainResource<PatientType> {
  identifier?: IIdentifier[];
  active?: boolean;
  name?: IHumanName[];
  telecom?: IContactPoint[];
  gender?: AdministrativeGender;
  birthDate?: date;
  deceasedBoolean?: boolean;
  deceasedDateTime?: dateTime;
  address?: IAddress;
  maritalStatus?: ICodeableConcept;
  multipleBirthBoolean?: boolean;
  multipleBirthInteger?: integer;
  photo?: IAttachment;
  contact?: IPatientContactComponent[];
  animal?: IPatientAnimalComponent;
  communication?: IPatientCommunicationComponent;
  generalPractitioner?: IReference;
  managingOrganization?: IReference;
  link?: IPatientLinkComponent[];
}
